/**
 * Portal de clientes — cada negocio enciende o apaga su portal desde aquí.
 * Vive en el hub de Configuración (programa `configuracion`; el backend
 * re-valida en permisos.py).
 *
 * Al activarlo por primera vez el servidor carga en segundo plano el índice
 * del negocio (qué préstamo es de qué cédula, sin montos) en la base
 * central; la pantalla muestra el avance y un botón para volver a cargar a
 * mano. No hay ningún proceso programado: el índice se refresca cada vez que
 * un cliente consulta.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, SettingSection, SettingRow } from "components/financepro";
import { useApi } from "lib/api";
import { basePortal } from "lib/portalqr";
import PortalKit from "modules/configuracion/portalkit";

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
// El tema no pone color de texto legible en los botones: va explícito, como en
// el resto de pantallas FinancePro.
const btnSolid = {
  ...btnBase,
  bgcolor: FP.color.navy,
  color: "#FFFFFF",
  "&:hover": { bgcolor: FP.color.navySoft, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${FP.color.navy}66`, color: "#FFFFFF" },
};
const btnOutline = {
  ...btnBase,
  color: FP.color.navy,
  bgcolor: "#FFFFFF",
  border: `1px solid ${FP.color.navy}`,
  "&:hover": {
    bgcolor: `${FP.color.navy}12`,
    border: `1px solid ${FP.color.navy}`,
    color: FP.color.navy,
  },
  "&.Mui-disabled": { color: `${FP.color.navy}66`, border: `1px solid ${FP.color.navy}44` },
};

// Dentro del hub el marco lo pone el panel derecho (mismo criterio que el
// resto de pantallas de Configuración).
const sectionBoxHub = { mb: 3 };
const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

const fechaHora = (iso) => {
  if (!iso) return "";
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}` : String(iso);
};

export default function PortalClientes({ enHub }) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const [estado, setEstado] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [whatsapp, setWhatsapp] = useState("");
  const sondeo = useRef(null);

  const cargar = useCallback(
    async (silencioso = false) => {
      if (!silencioso) setCargando(true);
      const { ok, data } = await post("/api/portal_config", {});
      if (!silencioso) setCargando(false);
      if (!ok) {
        showAlert({
          message: data.Error || "No se pudo leer el estado del portal",
          color: "error",
        });
        return null;
      }
      setEstado(data);
      setWhatsapp((w) => (w === "" ? data.whatsapp || "" : w));
      return data;
    },
    [post, showAlert]
  );

  useEffect(() => {
    cargar();
  }, [cargar]);

  // Mientras la carga del índice corre en el servidor, se consulta cada
  // pocos segundos para mostrar el avance.
  useEffect(() => {
    if (estado?.sincronizando && !sondeo.current) {
      sondeo.current = setInterval(() => cargar(true), 3000);
    }
    if (!estado?.sincronizando && sondeo.current) {
      clearInterval(sondeo.current);
      sondeo.current = null;
    }
    return () => {
      if (sondeo.current) {
        clearInterval(sondeo.current);
        sondeo.current = null;
      }
    };
  }, [estado?.sincronizando, cargar]);

  const guardar = async (cambios, mensaje) => {
    setGuardando(true);
    const { ok, data } = await post("/api/portal_config_guardar", cambios);
    setGuardando(false);
    if (!ok) {
      showAlert({ message: data.Error || "No se pudo guardar", color: "error" });
      return;
    }
    setEstado(data);
    showAlert({ message: mensaje, color: "success" });
  };

  const recargar = async () => {
    setGuardando(true);
    const { ok, data } = await post("/api/portal_recargar", {});
    setGuardando(false);
    if (!ok) {
      showAlert({ message: data.Error || "No se pudo iniciar la carga", color: "error" });
      return;
    }
    setEstado(data);
    showAlert({
      message: data.carga_iniciada ? "Carga del índice iniciada" : "Ya hay una carga en curso",
      color: data.carga_iniciada ? "success" : "warning",
    });
  };

  const url = basePortal(estado?.url || "/portal");
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(url);
      showAlert({ message: "Enlace copiado", color: "success" });
    } catch {
      showAlert({ message: url, color: "info" });
    }
  };

  const cabecera = (
    <PageHeader
      compact={enHub}
      icon={PhoneIphoneOutlinedIcon}
      title="Portal de Clientes"
      subtitle="Tus clientes consultan sus financiamientos desde el celular, sin mora y sin llamar."
    />
  );

  if (cargando && !estado) {
    return (
      <Box
        component="main"
        sx={enHub ? undefined : { minHeight: "100vh", bgcolor: FP.color.surface }}
      >
        {cabecera}
        <Box sx={{ py: 6, textAlign: "center" }}>
          <CircularProgress size={28} />
        </Box>
      </Box>
    );
  }

  const activo = Boolean(estado?.habilitado);

  let descripcionIndice =
    "Todavía no se ha cargado. Se carga solo al activar el portal por primera vez.";
  if (estado?.sincronizando) {
    descripcionIndice = "Cargando los préstamos del negocio en la base central…";
  } else if (estado?.error_sincronizacion) {
    descripcionIndice = `La última carga falló: ${estado.error_sincronizacion}`;
  } else if (estado?.sincronizado_en) {
    descripcionIndice = `Última carga ${fechaHora(
      estado.sincronizado_en
    )}. Después se refresca solo cada vez que un cliente consulta.`;
  }

  return (
    <Box
      component="main"
      sx={enHub ? undefined : { minHeight: "100vh", bgcolor: FP.color.surface }}
    >
      {cabecera}
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={enHub ? sectionBoxHub : sectionBox}>
          <SettingSection label="Estado">
            <SettingRow
              label="Portal activo"
              description={
                activo
                  ? "Tus clientes pueden entrar con su cédula y el celular registrado en la solicitud."
                  : "Apagado: este negocio no aparece en el portal aunque el cliente tenga financiamientos aquí."
              }
            >
              <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1}>
                <Chip
                  size="small"
                  label={activo ? "Activo" : "Apagado"}
                  sx={{
                    fontWeight: 700,
                    bgcolor: activo ? "#E3F3EA" : FP.color.border,
                    color: activo ? "#1F7A4D" : FP.color.textSecondary,
                  }}
                />
                <Switch
                  checked={activo}
                  disabled={guardando}
                  onChange={(e) =>
                    guardar(
                      { habilitado: e.target.checked },
                      e.target.checked ? "Portal activado" : "Portal apagado"
                    )
                  }
                />
              </Stack>
            </SettingRow>
            <SettingRow label="Índice de préstamos" description={descripcionIndice}>
              <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1.5}>
                {estado?.sincronizando && <CircularProgress size={18} />}
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: FP.color.textPrimary,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {Number(estado?.prestamos_indexados || 0).toLocaleString("es-DO")} préstamos
                </Typography>
                <Button
                  size="small"
                  sx={btnOutline}
                  disabled={guardando || Boolean(estado?.sincronizando)}
                  onClick={recargar}
                >
                  Volver a cargar
                </Button>
              </Stack>
            </SettingRow>
            <SettingRow
              label="WhatsApp del negocio"
              description="Botón de contacto que ve el cliente en cada financiamiento. Vacío: se usa el teléfono de la empresa."
              last
            >
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="809-000-0000"
                  value={whatsapp}
                  onChange={(e) => setWhatsapp(e.target.value)}
                  sx={fpInputSx}
                />
                <Button
                  size="small"
                  sx={{ ...btnSolid, flexShrink: 0 }}
                  disabled={guardando || whatsapp === (estado?.whatsapp || "")}
                  onClick={() => guardar({ whatsapp }, "WhatsApp guardado")}
                >
                  Guardar
                </Button>
              </Stack>
            </SettingRow>
          </SettingSection>

          <SettingSection label="Cómo entran tus clientes">
            <SettingRow
              label="Enlace del portal"
              description="Compártelo por WhatsApp o ponlo en el recibo. Desde el celular se puede instalar como app."
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                <Typography
                  sx={{ fontSize: "0.85rem", color: FP.color.textPrimary, wordBreak: "break-all" }}
                >
                  {url}
                </Typography>
                <Button
                  size="small"
                  sx={{ ...btnBase, minWidth: 0, color: FP.color.navy }}
                  onClick={copiar}
                  aria-label="Copiar enlace"
                >
                  <ContentCopyOutlinedIcon fontSize="small" />
                </Button>
              </Stack>
            </SettingRow>
            <SettingRow
              label="Acceso"
              description="Cédula, RNC o pasaporte más el celular que está en la solicitud del préstamo. Si el celular del cliente cambió, actualízalo en la solicitud para que pueda entrar."
              last
            >
              <Typography
                sx={{
                  fontSize: "0.85rem",
                  color: FP.color.textSecondary,
                  textAlign: { sm: "right" },
                }}
              >
                {estado?.verificador === "ninguno"
                  ? "Sin código de acceso (fase 1)"
                  : `Verificación: ${estado?.verificador}`}
              </Typography>
            </SettingRow>
          </SettingSection>

          {activo && (
            <SettingSection label="Difúndelo en tu estado de WhatsApp">
              <Box sx={{ py: 1.5 }}>
                <PortalKit negocio={estado?.nombre_comercial} ruta={estado?.url} />
              </Box>
            </SettingSection>
          )}

          <Typography sx={{ fontSize: "0.82rem", color: FP.color.textSecondary, mt: 1 }}>
            El cliente ve el pendiente sin mora, sus cuotas y sus recibos. Nunca ve datos de otros
            clientes ni de otros negocios que no sean los suyos.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

PortalClientes.propTypes = { enHub: PropTypes.bool };
PortalClientes.defaultProps = { enHub: false };
